import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../utils/api';
import InvoiceLinesTable from '../components/invoices/InvoiceLinesTable';
import InvoicePayments from '../components/invoices/InvoicePayments';
import {
  calculateBudgetTotals,
  downloadFile,
  getDayFromDate,
  getMonthFromDate,
  monthOptions,
  todayStr
} from '../utils/invoiceUtils';

const InvoiceFormPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const invoiceId = Number.isFinite(Number(id)) ? Number(id) : null;
  const clientIdRaw = searchParams.get('clientId');
  const clientIdParam = clientIdRaw && Number.isFinite(Number(clientIdRaw))
    ? Number(clientIdRaw)
    : null;
  const clientLocked = clientIdParam !== null;
  // Si llegamos desde la ficha del cliente (?clientId=), volver a su pestaña Facturas.
  const backTo = clientIdParam ? `/clientes/${clientIdParam}?tab=Facturas` : '/facturas';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [settings, setSettings] = useState({
    proformaSeries: '',
    proformaSeriesPad: 4,
    series: '',
    seriesPad: 4
  });
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [invoiceLineForm, setInvoiceLineForm] = useState({ id: null, concepto: '', cantidad: 1, precio: 0, descuento: 0, tvaTx: 21, periodStart: '', periodEnd: '' });
  const [loadingInvoiceLines, setLoadingInvoiceLines] = useState(false);
  const [invoiceLinesLoaded, setInvoiceLinesLoaded] = useState(false);
  const [autoNumber, setAutoNumber] = useState(true);
  const [numberLoading, setNumberLoading] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  // Guardado de línea en curso (evita que un doble clic duplique la línea
  // e incluso la factura al crearla con la primera línea).
  const [savingLine, setSavingLine] = useState(false);
  const [dolibarrLinked, setDolibarrLinked] = useState(false);

  const todayDate = new Date();
  const defaultRecurrentDay = todayDate.getDate();
  const defaultRecurrentMonth = todayDate.getMonth() + 1;
  const [invoiceForm, setInvoiceForm] = useState({
    id: invoiceId || null,
    clientId: clientIdParam || null,
    clientName: '',
    numero: '',
    fecha: todayStr(),
    fechaVencimiento: '',
    estado: 'Borrador',
    subtotal: '',
    iva: '',
    total: '',
    invoiceType: 'normal',
    serie: '',
    footerText: '',
    isRecurrent: false,
    recurrentUnit: 'months',
    recurrentInterval: 1,
    recurrentDayOfMonth: defaultRecurrentDay,
    recurrentMonthOfYear: defaultRecurrentMonth
  });

  const [clientOptions, setClientOptions] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);

  const handleDownloadFile = async (endpoint, filename) => {
    try {
      await downloadFile(endpoint, filename, token, handleUnauthorized);
    } catch (err) {
      alert(err.message);
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      const res = await apiFetch('/api/invoices/settings', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) {
        setSettings({
          proformaSeries: data?.proformaSeries || '',
          proformaSeriesPad: data?.proformaSeriesPad || 4,
          series: data?.series || '',
          seriesPad: data?.seriesPad || 4
        });
      }
    } catch (err) {
      console.error('Error cargando configuración de facturas', err);
    }
  }, [token, handleUnauthorized]);

  const fetchClients = useCallback(async (search = '') => {
    setClientsLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, pageSize: 20 });
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/clients?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando clientes');
      const options = (data?.data || []).map((c) => ({
        value: c.id,
        label: `${c.razon_social || `Cliente #${c.id}`}${c.email ? ` · ${c.email}` : ''}`,
        data: c
      }));
      setClientOptions(options);
    } catch (err) {
      console.error(err.message);
    } finally {
      setClientsLoading(false);
    }
  }, [token, handleUnauthorized]);

  const fetchClientSummary = useCallback(async (clientId) => {
    if (!clientId) return;
    try {
      const res = await apiFetch(`/api/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando cliente');
      setInvoiceForm((prev) => ({
        ...prev,
        clientId,
        clientName: data?.razon_social || prev.clientName
      }));
    } catch (err) {
      console.error(err.message);
    }
  }, [token, handleUnauthorized]);

  const fetchInvoice = useCallback(async (invoiceIdValue) => {
    if (!invoiceIdValue) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/invoices/${invoiceIdValue}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo factura');
      const readOnlyInvoice = Boolean(data?.dolibarr_invoice_id);
      setReadOnly(readOnlyInvoice);
      setDolibarrLinked(Boolean(data?.dolibarr_invoice_id));
      setInvoiceForm({
        id: data?.id || null,
        clientId: data?.client_id || null,
        clientName: data?.razon_social || '',
        numero: data?.numero || '',
        fecha: data?.fecha ? data.fecha.substring(0, 10) : todayStr(),
        fechaVencimiento: data?.fecha_vencimiento ? String(data.fecha_vencimiento).substring(0, 10) : '',
        estado: data?.estado || (data?.invoice_type === 'proforma' ? 'Proforma' : 'Borrador'),
        subtotal: data?.subtotal || '',
        iva: data?.iva || '',
        total: data?.total || '',
        invoiceType: data?.is_recurrent ? 'normal' : (['proforma','rectificativa'].includes(data?.invoice_type) ? data.invoice_type : 'normal'),
        rectificaInvoiceId: data?.rectifica_invoice_id || null,
        serie: data?.is_recurrent ? '' : (data?.serie || ''),
        footerText: data?.footer_text || data?.footerText || '',
        isRecurrent: Boolean(data?.is_recurrent),
        recurrentPaused: Boolean(data?.recurrent_paused),
        recurrentUnit: data?.recurrent_unit || 'months',
        recurrentInterval: Number(data?.recurrent_interval) >= 1 ? Number(data.recurrent_interval) : 1,
        recurrentDayOfMonth: data?.recurrent_day_of_month
          || getDayFromDate(data?.recurrent_next_date)
          || getDayFromDate(data?.fecha)
          || defaultRecurrentDay,
        recurrentMonthOfYear: data?.recurrent_month_of_year
          || getMonthFromDate(data?.recurrent_next_date)
          || getMonthFromDate(data?.fecha)
          || defaultRecurrentMonth
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [defaultRecurrentDay, defaultRecurrentMonth, token, handleUnauthorized]);

  const fetchInvoiceLines = useCallback(async (invoiceIdValue) => {
    if (!invoiceIdValue) return;
    setLoadingInvoiceLines(true);
    setInvoiceLinesLoaded(false);
    try {
      const res = await apiFetch(`/api/clients/invoices/${invoiceIdValue}/lines`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando líneas');
      setInvoiceLines(Array.isArray(data) ? data : []);
      setInvoiceLinesLoaded(true);
    } catch (err) {
      console.error(err);
      setInvoiceLines([]);
      setInvoiceLinesLoaded(false);
    } finally {
      setLoadingInvoiceLines(false);
    }
  }, [token, handleUnauthorized]);

  const fetchNextNumber = useCallback(async (type, serie) => {
    setNumberLoading(true);
    try {
      const params = new URLSearchParams();
      if (type) params.append('type', type);
      if (serie) params.append('serie', serie);
      const res = await apiFetch(`/api/invoices/next-number?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error generando número');
      setInvoiceForm((prev) => ({ ...prev, numero: data.number || prev.numero }));
    } catch (err) {
      console.error(err.message);
    } finally {
      setNumberLoading(false);
    }
  }, [token, handleUnauthorized]);

  const handleRecurrentToggle = (checked) => {
    // Desmarcar recurrente en una plantilla ya guardada la convierte en factura suelta
    // y deja de generar automáticamente: exigir confirmación (acción destructiva).
    if (!checked && invoiceForm.id && invoiceForm.isRecurrent) {
      if (!window.confirm('Esto convertirá la plantilla recurrente en una factura suelta y dejará de generar facturas automáticamente. ¿Continuar?')) return;
    }
    setInvoiceForm((prev) => {
      const next = { ...prev, isRecurrent: checked };
      if (checked) {
        next.invoiceType = 'normal';
        next.serie = '';
        next.numero = '';
        next.recurrentUnit = prev.recurrentUnit === 'years' ? 'years' : 'months';
        if (!prev.recurrentDayOfMonth) next.recurrentDayOfMonth = defaultRecurrentDay;
        if (!prev.recurrentMonthOfYear) next.recurrentMonthOfYear = defaultRecurrentMonth;
      }
      return next;
    });
    if (!checked && !invoiceForm.numero) {
      setAutoNumber(true);
      fetchNextNumber('normal', settings.series || '');
    }
  };

  const submitInvoice = async (e) => {
    e.preventDefault();
    // Evita el doble envío (y el cruce con el guardado de una línea, que puede
    // estar creando la factura en ese mismo momento)
    if (savingInvoice || savingLine) return;
    if (readOnly) return navigate(backTo);
    // El número no se exige en Borrador (se asigna al emitir) ni cuando el backend lo
    // autogenera (autoNumber). Solo en numeración manual de un documento numerado.
    const draftDeferNumber = invoiceForm.invoiceType !== 'proforma' && invoiceForm.estado === 'Borrador';
    if (!invoiceForm.isRecurrent && !draftDeferNumber && !autoNumber && !invoiceForm.numero) return alert('Número obligatorio');
    if (!invoiceForm.id && !invoiceForm.clientId) return alert('Selecciona un cliente');
    const linesKnown = !invoiceForm.id || invoiceLinesLoaded;
    if (invoiceForm.isRecurrent) {
      if (!invoiceForm.recurrentDayOfMonth || invoiceForm.recurrentDayOfMonth < 1 || invoiceForm.recurrentDayOfMonth > 31) {
        return alert('Selecciona un día válido para la recurrencia');
      }
      if (invoiceForm.recurrentUnit === 'years' && (!invoiceForm.recurrentMonthOfYear || invoiceForm.recurrentMonthOfYear < 1 || invoiceForm.recurrentMonthOfYear > 12)) {
        return alert('Selecciona un mes válido para la recurrencia anual');
      }
    }
    const submitType = invoiceForm.isRecurrent ? 'normal' : invoiceForm.invoiceType;
    if (!invoiceForm.isRecurrent && !invoiceLines.length && linesKnown && submitType !== 'proforma' && invoiceForm.estado !== 'Borrador') {
      return alert('Añade al menos una línea antes de cambiar el estado a Pendiente, Pagada o Anulada.');
    }
    if (submitType === 'proforma' && !invoiceForm.serie) {
      return alert('Serie requerida para proforma');
    }
    const isEdit = Boolean(invoiceForm.id);
    const url = isEdit ? `/api/clients/invoices/${invoiceForm.id}` : `/api/clients/${invoiceForm.clientId}/invoices`;
    const method = isEdit ? 'PUT' : 'POST';
    // Una plantilla recurrente no tiene estado fiscal: su on/off es recurrentPaused.
    const submitEstado = invoiceForm.isRecurrent ? null : (submitType === 'proforma' ? 'Proforma' : invoiceForm.estado);
    setSavingInvoice(true);
    try {
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          numero: invoiceForm.isRecurrent ? '' : invoiceForm.numero,
          fecha: invoiceForm.fecha,
          fechaVencimiento: invoiceForm.fechaVencimiento || null,
          estado: submitEstado,
          subtotal: invoiceForm.subtotal,
          iva: invoiceForm.iva,
          total: invoiceForm.total,
          footerText: invoiceForm.footerText,
          invoiceType: submitType,
          serie: submitType === 'proforma' ? invoiceForm.serie : null,
          isRecurrent: invoiceForm.isRecurrent,
          recurrentPaused: invoiceForm.isRecurrent ? Boolean(invoiceForm.recurrentPaused) : undefined,
          recurrentInterval: invoiceForm.isRecurrent ? (Number(invoiceForm.recurrentInterval) >= 1 ? Number(invoiceForm.recurrentInterval) : 1) : null,
          recurrentUnit: invoiceForm.isRecurrent ? invoiceForm.recurrentUnit : null,
          recurrentDayOfMonth: invoiceForm.isRecurrent ? invoiceForm.recurrentDayOfMonth : null,
          recurrentMonthOfYear: invoiceForm.isRecurrent && invoiceForm.recurrentUnit === 'years'
            ? invoiceForm.recurrentMonthOfYear
            : null,
          // Al crear, el backend autogenera el número (con reintento anti-colisión)
          // en vez de fiarse del de la vista previa.
          ...(isEdit ? {} : { autoNumber: autoNumber && !invoiceForm.isRecurrent })
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      if (!isEdit && data?.id) {
        const suffix = clientIdParam ? `?clientId=${clientIdParam}` : '';
        // Ir al formulario de edición (no al detalle de solo lectura) para poder
        // seguir añadiendo líneas sin salir del formulario.
        navigate(`/facturas/${data.id}/editar${suffix}`, { replace: true });
        return;
      }
      setInvoiceForm((prev) => ({
        ...prev,
        numero: data?.numero || prev.numero,
        fecha: data?.fecha ? data.fecha.substring(0, 10) : prev.fecha,
        estado: data?.estado || prev.estado,
        subtotal: data?.subtotal ?? prev.subtotal,
        iva: data?.iva ?? prev.iva,
        total: data?.total ?? prev.total,
        footerText: data?.footer_text ?? data?.footerText ?? prev.footerText
      }));
      alert('Factura guardada');
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingInvoice(false);
    }
  };

  const saveInvoiceLine = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    // Evita dobles clics: un segundo clic mientras guarda duplicaría la línea
    // (y hasta la propia factura al crearla junto a la primera línea).
    if (savingLine) return;
    if (!invoiceLineForm.concepto) return alert('Concepto obligatorio');
    setSavingLine(true);
    try {
      let invoiceIdValue = invoiceForm.id;
      if (!invoiceIdValue) {
        if (!invoiceForm.clientId) return alert('Selecciona un cliente');
        if (invoiceForm.isRecurrent) {
          if (!invoiceForm.recurrentDayOfMonth || invoiceForm.recurrentDayOfMonth < 1 || invoiceForm.recurrentDayOfMonth > 31) {
            return alert('Selecciona un día válido para la recurrencia');
          }
          if (invoiceForm.recurrentUnit === 'years' && (!invoiceForm.recurrentMonthOfYear || invoiceForm.recurrentMonthOfYear < 1 || invoiceForm.recurrentMonthOfYear > 12)) {
            return alert('Selecciona un mes válido para la recurrencia anual');
          }
        }
        const submitType = invoiceForm.isRecurrent ? 'normal' : invoiceForm.invoiceType;
        // Mismas validaciones que submitInvoice: número (salvo Borrador diferido o autoNumber)
        // y serie obligatoria para proforma, antes de crear la factura al añadir la 1ª línea.
        const draftDeferNumber = invoiceForm.invoiceType !== 'proforma' && invoiceForm.estado === 'Borrador';
        if (!invoiceForm.isRecurrent && !draftDeferNumber && !autoNumber && !invoiceForm.numero) return alert('Número obligatorio');
        if (submitType === 'proforma' && !invoiceForm.serie) return alert('Serie requerida para proforma');
        const submitEstado = submitType === 'proforma' ? 'Proforma' : (invoiceForm.estado || 'Borrador');
        const serieToUse = submitType === 'proforma' ? (invoiceForm.serie || settings.proformaSeries || 'PROF') : null;
        try {
          const res = await apiFetch(`/api/clients/${invoiceForm.clientId}/invoices`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              numero: invoiceForm.isRecurrent ? '' : invoiceForm.numero,
              fecha: invoiceForm.fecha,
              fechaVencimiento: invoiceForm.fechaVencimiento || null,
              estado: submitEstado,
              subtotal: invoiceForm.subtotal,
              iva: invoiceForm.iva,
              total: invoiceForm.total,
              footerText: invoiceForm.footerText,
              invoiceType: submitType,
              serie: submitType === 'proforma' ? serieToUse : null,
              isRecurrent: invoiceForm.isRecurrent,
              recurrentInterval: invoiceForm.isRecurrent ? (Number(invoiceForm.recurrentInterval) >= 1 ? Number(invoiceForm.recurrentInterval) : 1) : null,
              recurrentUnit: invoiceForm.isRecurrent ? invoiceForm.recurrentUnit : null,
              recurrentDayOfMonth: invoiceForm.isRecurrent ? invoiceForm.recurrentDayOfMonth : null,
              recurrentMonthOfYear: invoiceForm.isRecurrent && invoiceForm.recurrentUnit === 'years'
                ? invoiceForm.recurrentMonthOfYear
                : null,
              // Al crear, el backend autogenera el número (con reintento anti-colisión)
              // en vez de fiarse del de la vista previa.
              autoNumber: autoNumber && !invoiceForm.isRecurrent
            })
          });
          const data = await parseJsonSafe(res);
          if (res.status === 401) return handleUnauthorized();
          if (!res.ok) throw new Error(data.message || 'No se pudo crear la factura');
          invoiceIdValue = data?.id || null;
          if (!invoiceIdValue) throw new Error('No se pudo crear la factura');
          const suffix = clientIdParam ? `?clientId=${clientIdParam}` : '';
          navigate(`/facturas/${invoiceIdValue}/editar${suffix}`, { replace: true });
          setInvoiceForm((prev) => ({
            ...prev,
            id: invoiceIdValue,
            numero: data?.numero || prev.numero,
            fecha: data?.fecha ? data.fecha.substring(0, 10) : prev.fecha,
            estado: data?.estado || prev.estado,
            subtotal: data?.subtotal ?? prev.subtotal,
            iva: data?.iva ?? prev.iva,
            total: data?.total ?? prev.total,
            footerText: data?.footer_text ?? data?.footerText ?? prev.footerText
          }));
        } catch (err) {
          alert(err.message);
          return;
        }
      }
      const isEdit = Boolean(invoiceLineForm.id);
      const url = isEdit
        ? `/api/clients/invoices/${invoiceIdValue}/lines/${invoiceLineForm.id}`
        : `/api/clients/invoices/${invoiceIdValue}/lines`;
      const method = isEdit ? 'PUT' : 'POST';
      try {
        const res = await apiFetch(url, {
          method,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            concepto: invoiceLineForm.concepto,
            cantidad: invoiceLineForm.cantidad,
            precio: invoiceLineForm.precio,
            descuento: invoiceLineForm.descuento,
            tvaTx: invoiceLineForm.tvaTx,
            periodStart: invoiceLineForm.periodStart || null,
            periodEnd: invoiceLineForm.periodEnd || null
          })
        });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (!res.ok) throw new Error(data.message || 'Error guardando línea');
        setInvoiceLineForm({ id: null, concepto: '', cantidad: 1, precio: 0, descuento: 0, tvaTx: invoiceLineForm.tvaTx, periodStart: invoiceLineForm.periodStart, periodEnd: invoiceLineForm.periodEnd });
        fetchInvoiceLines(invoiceIdValue);
      } catch (err) {
        alert(err.message);
      }
    } finally {
      setSavingLine(false);
    }
  };

  const editInvoiceLine = (line) => {
    setInvoiceLineForm({
      id: line.id,
      concepto: line.concepto,
      cantidad: Number(line.cantidad || 1),
      precio: Number(line.precio || 0),
      descuento: Number(line.descuento || 0),
      tvaTx: line.tva_tx != null ? Number(line.tva_tx) : 21,
      periodStart: line.period_start ? line.period_start.substring(0, 10) : '',
      periodEnd: line.period_end ? line.period_end.substring(0, 10) : ''
    });
  };

  const deleteInvoiceLine = async (lineId) => {
    if (!invoiceForm.id) return;
    if (!window.confirm('¿Eliminar esta línea?')) return;
    try {
      const res = await apiFetch(`/api/clients/invoices/${invoiceForm.id}/lines/${lineId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error eliminando línea');
      fetchInvoiceLines(invoiceForm.id);
    } catch (err) {
      alert(err.message);
    }
  };

  // Factura guardada y ya numerada: el backend rechaza convertirla en plantilla
  // recurrente o en proforma, así que la UI bloquea esos checkboxes.
  // (Las plantillas recurrentes tienen numero vacío y siguen pudiendo desmarcarse.)
  const numberedInvoiceLock = Boolean(invoiceForm.id && invoiceForm.numero);
  // Borrador normal sin número: el número fiscal se asigna al emitir, no antes.
  const numberDeferred = !invoiceForm.isRecurrent && invoiceForm.invoiceType !== 'proforma' && invoiceForm.estado === 'Borrador' && !invoiceForm.numero;

  const selectedClientOption = useMemo(() => {
    if (!invoiceForm.clientId) return null;
    return clientOptions.find((opt) => opt.value === invoiceForm.clientId)
      || { value: invoiceForm.clientId, label: invoiceForm.clientName || `Cliente #${invoiceForm.clientId}` };
  }, [clientOptions, invoiceForm.clientId, invoiceForm.clientName]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoice(invoiceId);
      fetchInvoiceLines(invoiceId);
      return;
    }
    if (clientIdParam) fetchClientSummary(clientIdParam);
  }, [invoiceId, clientIdParam, fetchClientSummary, fetchInvoice, fetchInvoiceLines]);

  useEffect(() => {
    if (invoiceId || readOnly || clientLocked) return;
    const query = clientQuery.trim();
    const handler = setTimeout(() => {
      fetchClients(query);
    }, 250);
    return () => clearTimeout(handler);
  }, [clientQuery, invoiceId, readOnly, clientLocked, fetchClients]);

  useEffect(() => {
    // Las facturas Dolibarr son de solo lectura y sus líneas vienen con IVA
    // incluido: recalcular aquí corrompería sus totales.
    if (readOnly) return;
    if (!invoiceLinesLoaded) return;
    const { subtotalValue, ivaValue, totalValue } = calculateBudgetTotals(invoiceLines);
    const nextSubtotal = subtotalValue.toFixed(2);
    const nextIva = ivaValue.toFixed(2);
    const nextTotal = totalValue.toFixed(2);
    setInvoiceForm((prev) => {
      if (prev.subtotal === nextSubtotal && prev.iva === nextIva && prev.total === nextTotal) return prev;
      return {
        ...prev,
        subtotal: nextSubtotal,
        iva: nextIva,
        total: nextTotal
      };
    });
  }, [invoiceLines, invoiceLinesLoaded, readOnly]);

  useEffect(() => {
    if (invoiceId || readOnly || invoiceForm.id) return;
    if (!autoNumber || invoiceForm.isRecurrent) return;
    // Un borrador normal NO recibe número hasta emitir: no pedir vista previa.
    if (invoiceForm.invoiceType !== 'proforma' && invoiceForm.estado === 'Borrador') return;
    const handler = setTimeout(() => {
      if (invoiceForm.invoiceType === 'proforma') {
        const nextSerie = (invoiceForm.serie || settings.proformaSeries || 'PROF').trim();
        fetchNextNumber('proforma', nextSerie);
      } else {
        fetchNextNumber('normal', settings.series || '');
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [invoiceId, readOnly, invoiceForm.id, invoiceForm.invoiceType, invoiceForm.serie, invoiceForm.estado, autoNumber, invoiceForm.isRecurrent, settings.proformaSeries, settings.series, fetchNextNumber]);

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">
            {invoiceForm.id ? (readOnly ? 'Factura (solo lectura)' : 'Editar factura') : 'Nueva factura'}
          </h2>
          <div className="text-muted small">
            {invoiceForm.clientId ? (
              <Link to={`/clientes/${invoiceForm.clientId}`} title="Ir a la ficha del cliente">
                {invoiceForm.clientName || `Cliente #${invoiceForm.clientId}`}
              </Link>
            ) : 'Selecciona un cliente'}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(backTo)}>
            Volver
          </button>
          {!readOnly && (
            <button className="btn btn-primary btn-sm" onClick={submitInvoice} disabled={savingInvoice || numberLoading || savingLine}>
              {savingInvoice ? 'Guardando...' : numberLoading ? 'Generando número...' : invoiceForm.id ? 'Actualizar' : 'Guardar'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {readOnly && (
        <div className="alert alert-warning py-2">
          Esta factura está sincronizada desde Dolibarr. Solo puedes verla aquí; edítala desde Dolibarr.
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          {loading ? (
            <p>Cargando...</p>
          ) : (
            <>
              {!invoiceForm.id ? (
                <div className="mb-3">
                  <label className="form-label small">Cliente *</label>
                  <Select
                    classNamePrefix="select"
                    isLoading={clientsLoading}
                    options={clientOptions}
                    value={selectedClientOption}
                    placeholder="Selecciona cliente"
                    onChange={(opt) => setInvoiceForm((p) => ({ ...p, clientId: opt ? opt.value : null, clientName: opt?.label || '' }))}
                    onInputChange={(val, action) => {
                      if (action.action === 'input-change') setClientQuery(val);
                      return val;
                    }}
                    noOptionsMessage={() => 'Sin resultados'}
                    isDisabled={readOnly || clientLocked}
                  />
                </div>
              ) : (
                <div className="mb-3">
                  <label className="form-label small">Cliente</label>
                  <input
                    className="form-control"
                    value={invoiceForm.clientName || `Cliente #${invoiceForm.clientId || '-'}`}
                    disabled
                  />
                </div>
              )}
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small">{numberDeferred ? 'Número' : 'Número *'}</label>
                  {numberDeferred ? (
                    <input className="form-control" value="" disabled placeholder="Se asignará al emitir" />
                  ) : (
                    <input
                      className="form-control"
                      value={invoiceForm.numero}
                      onChange={(e) => { setAutoNumber(false); setInvoiceForm((p) => ({ ...p, numero: e.target.value })); }}
                      required={!invoiceForm.isRecurrent}
                      disabled={readOnly || invoiceForm.isRecurrent}
                      placeholder={invoiceForm.isRecurrent ? 'El número se asigna a cada factura generada' : ''}
                    />
                  )}
                  {numberLoading && <div className="text-muted small mt-1">Generando número...</div>}
                  {numberDeferred && <div className="text-muted small mt-1">El número fiscal se asigna al emitir (pasar a Pendiente).</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Fecha</label>
                  <input className="form-control" type="date" value={invoiceForm.fecha} onChange={(e) => setInvoiceForm((p) => ({ ...p, fecha: e.target.value }))} disabled={readOnly} />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Vencimiento</label>
                  <input className="form-control" type="date" value={invoiceForm.fechaVencimiento || ''} onChange={(e) => setInvoiceForm((p) => ({ ...p, fechaVencimiento: e.target.value }))} disabled={readOnly} />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">{invoiceForm.isRecurrent ? 'Activación' : 'Estado'}</label>
                  {invoiceForm.isRecurrent ? (
                    // La recurrente es una plantilla: su único interruptor es Activa/Desactivada
                    // (recurrent_paused). Nada de estados fiscales.
                    <select
                      className="form-select"
                      value={invoiceForm.recurrentPaused ? 'off' : 'on'}
                      onChange={(e) => setInvoiceForm((p) => ({ ...p, recurrentPaused: e.target.value === 'off' }))}
                      disabled={readOnly || !invoiceForm.id}
                      title={!invoiceForm.id ? 'La recurrente se crea activa; puedes desactivarla después' : undefined}
                    >
                      <option value="on">Activa</option>
                      <option value="off">Desactivada</option>
                    </select>
                  ) : invoiceForm.invoiceType === 'proforma' ? (
                    <input className="form-control" value="Proforma" disabled />
                  ) : (
                    <select
                      className="form-select"
                      value={invoiceForm.estado}
                      onChange={(e) => setInvoiceForm((p) => ({ ...p, estado: e.target.value }))}
                      disabled={readOnly}
                    >
                      {/* Estados no editables a mano (Parcial/Devuelta/Validada de Dolibarr,
                          Anulada -> usar el botón Anular): se muestran como opción para
                          reflejar el valor real, pero no se ofrecen para cambiar a mano. */}
                      {invoiceForm.estado
                        && !['Borrador', 'Pendiente', 'Pagada'].includes(invoiceForm.estado) && (
                        <option value={invoiceForm.estado}>{invoiceForm.estado}</option>
                      )}
                      <option value="Borrador">Borrador</option>
                      <option value="Pendiente">Pendiente</option>
                      <option value="Pagada">Pagada</option>
                    </select>
                  )}
                </div>
                {!readOnly && !invoiceForm.isRecurrent && (
                  <div className="col-12">
                    <div className="form-check mt-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="invoiceProformaCheck"
                        checked={invoiceForm.invoiceType === 'proforma'}
                        disabled={readOnly || (numberedInvoiceLock && invoiceForm.invoiceType !== 'proforma')}
                        title={numberedInvoiceLock && invoiceForm.invoiceType !== 'proforma'
                          ? 'Una factura emitida no puede convertirse en proforma'
                          : undefined}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const nextSerie = checked ? (invoiceForm.serie || settings.proformaSeries || 'PROF') : '';
                          setInvoiceForm((p) => ({
                            ...p,
                            invoiceType: checked ? 'proforma' : 'normal',
                            serie: nextSerie,
                            estado: checked ? 'Proforma' : (p.estado === 'Proforma' ? 'Borrador' : p.estado)
                          }));
                          setAutoNumber(true);
                          fetchNextNumber(checked ? 'proforma' : 'normal', checked ? nextSerie : settings.series || '');
                        }}
                      />
                      <label className="form-check-label" htmlFor="invoiceProformaCheck">
                        Crear como proforma
                      </label>
                    </div>
                  </div>
                )}
                {!readOnly && invoiceForm.invoiceType === 'proforma' && (
                  <div className="col-md-4">
                    <label className="form-label small">Serie proforma</label>
                    <input
                      className="form-control"
                      value={invoiceForm.serie}
                      onChange={(e) => {
                        const nextSerie = e.target.value;
                        setInvoiceForm((p) => ({ ...p, serie: nextSerie }));
                        setAutoNumber(true);
                      }}
                      placeholder={settings.proformaSeries || 'PROF'}
                    />
                  </div>
                )}
                <div className="col-md-3">
                  <label className="form-label small">Base imponible</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    value={invoiceForm.subtotal}
                    disabled
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">IVA</label>
                  <input className="form-control" type="number" step="0.01" value={invoiceForm.iva} disabled />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Total</label>
                  <input className="form-control" type="number" step="0.01" value={invoiceForm.total} disabled />
                </div>
                <div className="col-12">
                  <label className="form-label small">Texto pie de factura</label>
                  <input
                    className="form-control"
                    value={invoiceForm.footerText}
                    onChange={(e) => setInvoiceForm((p) => ({ ...p, footerText: e.target.value }))}
                    placeholder="Texto opcional a mostrar en el PDF"
                    disabled={readOnly}
                  />
                </div>
                <div className="col-12">
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="invoiceRecurrentCheck"
                      checked={invoiceForm.isRecurrent}
                      onChange={(e) => handleRecurrentToggle(e.target.checked)}
                      disabled={readOnly || (numberedInvoiceLock && !invoiceForm.isRecurrent)}
                      title={numberedInvoiceLock && !invoiceForm.isRecurrent
                        ? 'Una factura emitida no puede convertirse en plantilla recurrente'
                        : undefined}
                    />
                    <label className="form-check-label" htmlFor="invoiceRecurrentCheck">
                      Factura recurrente
                    </label>
                  </div>
                </div>
                {invoiceForm.isRecurrent && (
                  <>
                    <div className="col-md-3">
                      <label className="form-label small">Frecuencia</label>
                      <select
                        className="form-select"
                        value={invoiceForm.recurrentUnit}
                        onChange={(e) => setInvoiceForm((p) => ({ ...p, recurrentUnit: e.target.value }))}
                        disabled={readOnly}
                      >
                        <option value="months">Mensual</option>
                        <option value="years">Anual</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small">
                        Cada ({invoiceForm.recurrentUnit === 'years' ? 'años' : 'meses'})
                      </label>
                      <input
                        className="form-control"
                        type="number"
                        min="1"
                        max="36"
                        value={invoiceForm.recurrentInterval}
                        onChange={(e) => setInvoiceForm((p) => ({ ...p, recurrentInterval: Math.max(1, Number(e.target.value) || 1) }))}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Día del mes</label>
                      <input
                        className="form-control"
                        type="number"
                        min="1"
                        max="31"
                        value={invoiceForm.recurrentDayOfMonth}
                        onChange={(e) => setInvoiceForm((p) => ({ ...p, recurrentDayOfMonth: Number(e.target.value || 1) }))}
                        disabled={readOnly}
                      />
                    </div>
                    {invoiceForm.recurrentUnit === 'years' && (
                      <div className="col-md-4">
                        <label className="form-label small">Mes</label>
                        <select
                          className="form-select"
                          value={invoiceForm.recurrentMonthOfYear}
                          onChange={(e) => setInvoiceForm((p) => ({ ...p, recurrentMonthOfYear: Number(e.target.value) }))}
                          disabled={readOnly}
                        >
                          {monthOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Líneas de factura</h6>
                  {loadingInvoiceLines && <span className="text-muted small">Cargando...</span>}
                </div>
                {readOnly && (
                  <div className="alert alert-info mb-2">Las líneas se gestionan en Dolibarr. Aquí puedes consultarlas.</div>
                )}
                {readOnly ? (
                  <InvoiceLinesTable lines={invoiceLines} showActions={false} />
                ) : (
                  <>
                    <div className="row g-2 mb-2">
                      <div className="col-md-3">
                        <label className="form-label small mb-0">Concepto</label>
                        <input className="form-control" value={invoiceLineForm.concepto} onChange={(e) => setInvoiceLineForm((p) => ({ ...p, concepto: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small mb-0">Periodo inicio</label>
                        <input className="form-control" type="date" value={invoiceLineForm.periodStart} onChange={(e) => setInvoiceLineForm((p) => ({ ...p, periodStart: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small mb-0">Periodo fin</label>
                        <input className="form-control" type="date" value={invoiceLineForm.periodEnd} onChange={(e) => setInvoiceLineForm((p) => ({ ...p, periodEnd: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small mb-0">Cantidad</label>
                        <input className="form-control" type="number" step="0.01" value={invoiceLineForm.cantidad} onChange={(e) => setInvoiceLineForm((p) => ({ ...p, cantidad: Number(e.target.value) }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small mb-0">Precio</label>
                        <input className="form-control" type="number" step="0.01" value={invoiceLineForm.precio} onChange={(e) => setInvoiceLineForm((p) => ({ ...p, precio: Number(e.target.value) }))} />
                      </div>
                      <div className="col-md-1">
                        <label className="form-label small mb-0">Dto.%</label>
                        <input className="form-control" type="number" step="0.01" min="0" max="100" value={invoiceLineForm.descuento} onChange={(e) => setInvoiceLineForm((p) => ({ ...p, descuento: Number(e.target.value) }))} />
                      </div>
                      <div className="col-md-1">
                        <label className="form-label small mb-0">IVA</label>
                        <select className="form-select" value={invoiceLineForm.tvaTx} onChange={(e) => setInvoiceLineForm((p) => ({ ...p, tvaTx: Number(e.target.value) }))}>
                          <option value={21}>21%</option>
                          <option value={10}>10%</option>
                          <option value={4}>4%</option>
                          <option value={0}>0%</option>
                        </select>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mb-2">
                      <button className="btn btn-sm btn-primary" type="button" onClick={saveInvoiceLine} disabled={numberLoading || savingInvoice || savingLine}>
                        {savingLine ? 'Guardando…' : invoiceLineForm.id ? 'Actualizar' : 'Añadir línea y continuar'}
                      </button>
                    </div>
                    <InvoiceLinesTable
                      lines={invoiceLines}
                      onEdit={editInvoiceLine}
                      onDelete={deleteInvoiceLine}
                      showActions
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Ni proformas ni rectificativas admiten pagos, y las plantillas recurrentes no son facturas. */}
      {invoiceForm.id && invoiceForm.invoiceType !== 'proforma' && invoiceForm.invoiceType !== 'rectificativa' && !invoiceForm.isRecurrent && (
        <InvoicePayments
          invoiceId={invoiceForm.id}
          invoiceTotal={invoiceForm.total}
          dolibarrLinked={dolibarrLinked}
        />
      )}

      {invoiceForm.id && (
        <div className="d-flex gap-2">
          {readOnly && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleDownloadFile(`/api/clients/invoices/${invoiceForm.id}/dolibarr-pdf`, `${invoiceForm.numero || 'factura'}.pdf`)}
            >
              PDF Dolibarr
            </button>
          )}
          {!invoiceForm.isRecurrent && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleDownloadFile(`/api/clients/invoices/${invoiceForm.id}/pdf`, `${invoiceForm.numero || 'factura'}.pdf`)}
            >
              PDF
            </button>
          )}
          {invoiceForm.invoiceType !== 'proforma' && invoiceForm.invoiceType !== 'rectificativa' && !dolibarrLinked
            && !invoiceForm.isRecurrent && invoiceForm.estado !== 'Borrador' && (
            <button
              className="btn btn-outline-warning btn-sm"
              type="button"
              onClick={async () => {
                if (!window.confirm('¿Crear factura rectificativa a partir de esta factura?')) return;
                try {
                  const res = await apiFetch(`/api/clients/invoices/${invoiceForm.id}/rectificar`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  const data = await parseJsonSafe(res);
                  if (res.status === 401) return handleUnauthorized();
                  if (!res.ok) throw new Error(data.message || 'Error creando rectificativa');
                  navigate(`/facturas/${data.id}`);
                } catch (err) {
                  alert(err.message);
                }
              }}
            >
              Crear rectificativa
            </button>
          )}
          {clientIdParam && (
            <Link className="btn btn-outline-secondary btn-sm" to={backTo}>
              Volver al cliente
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceFormPage;
